import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { useNavigate, Navigate } from 'react-router-dom';
import { apiPostPublic, resetPassword } from '../api';
import { useUserStore, userToUserData } from '../stores/userStore';
import logo from '../assets/images/logo.png';
import { LoginResponse } from '../types/api';
import { isAuthenticationError } from '../utils/authErrors';
import { isValidEmail, VALIDATION_MESSAGES } from '../utils/validation';
import { safeStorage } from '../utils/safeStorage';
import Modal from '../components/Modal';
import Input from '../components/Input';
import PasswordInput from '../components/PasswordInput';
import Button from '../components/Button';

const SignInScreen: React.FC = () => {
  const { t } = useTranslation();
  // Email/password login state
  const [form, setForm] = useState({
    email: '',
    password: '',
  });

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [submitting, setSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [rememberMe, setRememberMe] = useState(true);

  // Forgot password state
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSubmitting, setForgotSubmitting] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSuccess, setForgotSuccess] = useState('');

  const navigate = useNavigate();

  // Zustand store
  const { setUser, setToken, isAuthenticated, hasToken } = useUserStore();

  // If already logged in, route based on account type
  const { user: currentUser } = useUserStore();
  if (isAuthenticated && hasToken) {
    return <Navigate to={currentUser?.is_employer ? '/employer-dashboard' : '/dashboard'} replace />;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: '' });
    setApiError('');
  };

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedEmail = forgotEmail.trim();

    if (!trimmedEmail) {
      setForgotError(VALIDATION_MESSAGES.EMAIL_REQUIRED);
      setForgotSuccess('');
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setForgotError(VALIDATION_MESSAGES.EMAIL_INVALID);
      setForgotSuccess('');
      return;
    }

    setForgotError('');
    setForgotSuccess('');
    setForgotSubmitting(true);

    try {
      const response = await resetPassword(trimmedEmail);

      if (response.success) {
        setForgotSuccess(
          response.message ||
          'If that email exists, a reset link has been sent.'
        );
      } else {
        setForgotError(
          response.message || 'Password reset failed. Please try again.'
        );
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Password reset failed. Please try again.';

      console.error('Forgot password error:', {
        error: errorMessage,
        stack: error.stack,
      });

      setForgotError(errorMessage);
    } finally {
      setForgotSubmitting(false);
    }
  };

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    if (!form.email.trim()) newErrors.email = t('login.email_required');
    if (!form.password) newErrors.password = t('login.password_required');
    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setErrors({});
    setSubmitting(true);
    setApiError('');

    try {
      // Simple email/password login
      const loginResponse = await apiPostPublic<LoginResponse>(
        '/api/auth/login',
        {
          email: form.email,
          password: form.password,
        }
      );

      if (loginResponse.success) {
        // Store the Firebase ID token (always present on successful login)
        setToken(loginResponse.token);

        // Convert API response to store format
        const userData = userToUserData(loginResponse.user);
        setUser(userData);

        // Session persistence is handled entirely by Zustand persist (userStore).
        // No need to store the user object separately — avoids sensitive data in localStorage.
        if (!rememberMe) {
          safeStorage.removeItem('user');
          safeStorage.removeItem('rememberMe');
        }

        // Check for redirect URL — only allow internal paths
        const searchParams = new URLSearchParams(window.location.search);
        const redirectUrl = searchParams.get('redirect');
        // Employers land on their dashboard, workers on theirs
        const defaultDest = userData.is_employer ? '/employer-dashboard' : '/dashboard';
        const safeRedirect = redirectUrl && redirectUrl.startsWith('/') && !redirectUrl.startsWith('//') ? redirectUrl : defaultDest;
        navigate(safeRedirect);
      } else {
        setApiError(t('login.login_failed'));
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        'Login failed. Please try again.';

      // Log structured error
      console.error('Login error:', {
        error: errorMessage,
      });

      // Capture error in Sentry (only for non-auth errors to avoid noise)
      if (
        process.env.REACT_APP_SENTRY_DSN &&
        !isAuthenticationError(errorMessage)
      ) {
        Sentry.captureException(error, {
          tags: {
            screen: 'LoginScreen',
            action: 'login',
            error_type: 'authentication',
          },
          extra: {
            errorMessage,
          },
        });
      }

      // Set user-friendly error message
      setApiError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-8">
      <div className="max-w-md mx-auto px-4">
        <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
          {/* Logo and Header */}
          <div className="text-center mb-6">
            <div className="flex items-center justify-center mb-2">
              <img src={logo} alt="VisaConnect Logo" className="h-16 w-auto" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">{t('login.welcome_back')}</h1>
            <p className="text-sm text-gray-600">{t('login.sign_in_subtitle')}</p>
          </div>

          {/* Email/Password Login Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {apiError && (
              <div className="w-full bg-red-50 border border-red-200 rounded-lg p-3 text-left">
                <p className="text-red-600 text-sm">{apiError}</p>
              </div>
            )}

            <Input
              label={t('login.email')}
              type="email"
              name="email"
              placeholder={t('login.email')}
              value={form.email}
              onChange={handleChange}
              error={errors.email}
              disabled={submitting}
              required
            />

            <div>
              <PasswordInput
                label={t('login.password')}
                name="password"
                placeholder={t('login.password')}
                value={form.password}
                onChange={handleChange}
                error={errors.password}
                disabled={submitting}
                required
              />
              <div className="flex justify-end mt-1">
                <button
                  type="button"
                  className="text-cyan-600 hover:text-cyan-700 text-sm font-medium"
                  onClick={() => navigate('/forgot-password')}
                >
                  {t('login.forgot_password')}
                </button>
              </div>
            </div>

            {/* Remember me */}
            <div className="flex items-center gap-2 text-left">
              <input
                id="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
              />
              <label htmlFor="remember-me" className="text-sm text-gray-600 cursor-pointer select-none">
                {t('login.keep_logged_in')}
              </label>
            </div>

            <Button type="submit" disabled={submitting} className="w-full mt-2">
              {submitting ? t('login.signing_in') : t('login.sign_in')}
            </Button>
          </form>

          {/* Back to Welcome */}
          <div className="mt-6 pt-6 border-t border-gray-100">
            <p className="text-sm text-gray-600">
              {t('login.no_account')}{' '}
              <button
                className="text-cyan-600 hover:text-cyan-700 font-medium"
                onClick={() => navigate('/create-account')}
              >
                {t('login.create_account')}
              </button>
            </p>
            <button
              className="text-gray-500 hover:text-gray-700 text-sm mt-4 underline"
              onClick={() => navigate('/')}
            >
              {t('login.back_to_welcome')}
            </button>
          </div>
        </div>

        {/* Forgot Password Modal */}
        <Modal
          isOpen={forgotPasswordOpen}
          onClose={() => {
            if (forgotSubmitting) {
              return;
            }
            setForgotPasswordOpen(false);
          }}
          title={t('login.reset_password_title')}
          size="lg"
        >
          <form onSubmit={handleForgotPasswordSubmit} className="space-y-4">
            <p className="text-sm text-gray-600">
              {t('login.reset_password_desc')}
            </p>
            <div>
              <label
                htmlFor="reset-password-email"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                {t('login.email')}
              </label>
              <input
                id="reset-password-email"
                type="email"
                value={forgotEmail}
                onChange={(e) => {
                  setForgotEmail(e.target.value);
                  setForgotError('');
                  setForgotSuccess('');
                }}
                placeholder={t('login.email')}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                disabled={forgotSubmitting}
                aria-label={t('login.reset_password_email_aria')}
                aria-required="true"
                autoFocus
              />
            </div>
            {forgotError && (
              <p className="text-sm text-red-600 mt-1">{forgotError}</p>
            )}
            {forgotSuccess && (
              <p className="text-sm text-green-600 mt-1">{forgotSuccess}</p>
            )}
            <div className="flex flex-col gap-3 pt-4">
              <Button
                type="submit"
                disabled={forgotSubmitting}
                className="w-full"
              >
                {forgotSubmitting ? t('login.sending') : t('login.send_reset_link')}
              </Button>
              <button
                type="button"
                onClick={() => {
                  if (forgotSubmitting) {
                    return;
                  }
                  setForgotPasswordOpen(false);
                }}
                className="text-gray-500 underline text-sm self-center hover:text-gray-700"
              >
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </Modal>
      </div>
    </div>
  );
};

export default SignInScreen;

